import React, { useState, useEffect, useRef } from 'react';
import { Navigation, MapPin, Truck } from 'lucide-react';

/**
 * RouteMapVisualization Component
 * 
 * Displays multi-trip route optimization on a map with:
 * - Pickup/dropoff points numbered (1, 2, 3...)
 * - Driving routes between destinations
 * - Deadhead trails (dashed lines) when dropoff != next pickup
 * - Payment/profit information at each stop
 */
function RouteMapVisualization({ routeData, origin, isDarkMode = false, mapBackground = 'light' }) {
  const [map, setMap] = useState(null);
  const mapContainer = useRef(null);

  // Initialize Mapbox map
  useEffect(() => {
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    
    if (!MAPBOX_API_KEY || !mapContainer.current) {
      console.warn('[RouteMapVisualization] Missing API key or container');
      return;
    }

    if (!MAPBOX_API_KEY.startsWith('pk.')) {
      console.error('[RouteMapVisualization] Mapbox GL requires a public access token (pk.*)');
      return;
    }

    console.log('[RouteMapVisualization] Initializing map...');
    
    // Dynamically import mapbox-gl
    import('mapbox-gl').then((mapboxgl) => {
      mapboxgl.default.accessToken = MAPBOX_API_KEY;
      
      // Get map style based on background and dark mode
      const getMapStyle = () => {
        if (mapBackground === 'satellite') return 'mapbox://styles/mapbox/satellite-streets-v12';
        if (mapBackground === 'dark' || isDarkMode) return 'mapbox://styles/mapbox/dark-v11';
        return 'mapbox://styles/mapbox/streets-v12';
      };
      
      const mapInstance = new mapboxgl.default.Map({
        container: mapContainer.current,
        style: getMapStyle(),
        center: [-95.7129, 37.0902], // Center of US as default
        zoom: 4
      });

      mapInstance.on('load', () => {
        console.log('[RouteMapVisualization] Map loaded successfully');
        setMap(mapInstance);
      });

      mapInstance.on('error', (e) => {
        console.error('[RouteMapVisualization] Mapbox error:', e);
      });

      return () => {
        console.log('[RouteMapVisualization] Cleaning up map instance');
        mapInstance.remove();
      };
    }).catch((error) => {
      console.error('[RouteMapVisualization] Failed to load Mapbox GL:', error);
    });
  }, []); // Only initialize once

  // Update map style when background or dark mode changes
  useEffect(() => {
    if (!map) return;
    
    const getMapStyle = () => {
      if (mapBackground === 'satellite') return 'mapbox://styles/mapbox/satellite-streets-v12';
      if (mapBackground === 'dark' || isDarkMode) return 'mapbox://styles/mapbox/dark-v11';
      return 'mapbox://styles/mapbox/streets-v12';
    };
    
    const newStyle = getMapStyle();
    console.log('[RouteMapVisualization] Updating map style to:', newStyle);
    map.setStyle(newStyle);
  }, [map, mapBackground, isDarkMode]);

  // Geocode city names to coordinates
  const geocodeCity = async (cityName) => {
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    if (!MAPBOX_API_KEY) return null;

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(cityName)}.json?access_token=${MAPBOX_API_KEY}&country=US&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [lon, lat] = data.features[0].center;
        return { lat, lon };
      }
    } catch (error) {
      console.error('[RouteMapVisualization] Geocoding error for', cityName, ':', error);
    }
    
    return null;
  };

  // Draw route between two points using Mapbox Directions API with optional trip details
  const drawRoute = async (map, startCoords, endCoords, routeId, isDashed = false, tripInfo = null) => {
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    if (!MAPBOX_API_KEY) return null;

    try {
      const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${startCoords.lon},${startCoords.lat};${endCoords.lon},${endCoords.lat}?geometries=geojson&access_token=${MAPBOX_API_KEY}`;
      
      const response = await fetch(directionsUrl);
      const data = await response.json();
      
      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        const distanceMiles = (route.distance / 1609.34);
        const durationMinutes = route.duration / 60;
        const durationHours = durationMinutes / 60;
        
        // Add route source
        map.addSource(routeId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              distance: distanceMiles.toFixed(1),
              duration: Math.round(durationMinutes),
              durationHours: durationHours.toFixed(1)
            },
            geometry: route.geometry
          }
        });
        
        // Add route layer
        map.addLayer({
          id: routeId,
          type: 'line',
          source: routeId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': isDashed ? '#94a3b8' : '#3b82f6', // Gray for deadhead, blue for routes
            'line-width': isDashed ? 3 : 4,
            'line-opacity': isDashed ? 0.6 : 0.8,
            'line-dasharray': isDashed ? [2, 2] : [1, 0] // Dashed for deadhead
          }
        });
        
        // Add route label with driving details if this is a loaded trip (not deadhead)
        if (!isDashed && tripInfo) {
          const midpointIndex = Math.floor(route.geometry.coordinates.length / 2);
          const midpoint = route.geometry.coordinates[midpointIndex];
          
          const labelId = `${routeId}-label`;
          map.addSource(labelId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {
                payment: tripInfo.payment || tripInfo.payment_amount || 0,
                profit: tripInfo.profit || 0,
                distance: Math.round(distanceMiles),
                duration: durationHours.toFixed(1),
                durationDesc: `${Math.floor(durationHours)}h${Math.round((durationHours % 1) * 60)}m`
              },
              geometry: {
                type: 'Point',
                coordinates: midpoint
              }
            }
          });
          
          map.addLayer({
            id: labelId,
            type: 'symbol',
            source: labelId,
            layout: {
              'text-field': [
                'concat',
                '$', ['get', 'payment'],
                '\n',
                ['get', 'distance'], ' mi • ', ['get', 'durationDesc']
              ],
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-size': 11,
              'text-anchor': 'center',
              'text-offset': [0, 0]
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#3b82f6',
              'text-halo-width': 2
            }
          });
        } else if (isDashed) {
          // Add deadhead label
          const midpointIndex = Math.floor(route.geometry.coordinates.length / 2);
          const midpoint = route.geometry.coordinates[midpointIndex];
          
          const labelId = `${routeId}-label`;
          map.addSource(labelId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {
                distance: Math.round(distanceMiles),
                duration: durationHours.toFixed(1),
                durationDesc: `${Math.floor(durationHours)}h${Math.round((durationHours % 1) * 60)}m`
              },
              geometry: {
                type: 'Point',
                coordinates: midpoint
              }
            }
          });
          
          map.addLayer({
            id: labelId,
            type: 'symbol',
            source: labelId,
            layout: {
              'text-field': [
                'concat',
                'Deadhead\n',
                ['get', 'distance'], ' mi • ', ['get', 'durationDesc']
              ],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 10,
              'text-anchor': 'center',
              'text-offset': [0, 0]
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#94a3b8',
              'text-halo-width': 2
            }
          });
        }
        
        return {
          distance: distanceMiles,
          duration: durationHours,
          durationDesc: `${Math.floor(durationHours)}h${Math.round((durationHours % 1) * 60)}m`
        };
      }
    } catch (error) {
      console.error('[RouteMapVisualization] Error drawing route:', error);
    }
    return null;
  };

  // Add pickup/dropoff markers
  const addTripMarkers = (map, trips, originCoords) => {
    // Clear existing marker layers and sources only (preserve route layers)
    const clearMarkerLayers = () => {
      const existingLayers = map.getStyle().layers.map(layer => layer.id);
      const existingSources = Object.keys(map.getStyle().sources);
      
      // Remove only trip marker layers and sources, preserve route and deadhead layers
      existingLayers.forEach(layerId => {
        if (layerId.startsWith('trip-')) {
          try {
            map.removeLayer(layerId);
          } catch (e) {}
        }
      });
      
      existingSources.forEach(sourceId => {
        if (sourceId.startsWith('trip-')) {
          try {
            map.removeSource(sourceId);
          } catch (e) {}
        }
      });
    };

    clearMarkerLayers();

    // Add origin marker
    map.addSource('trip-origin', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {
          label: 'START',
          city: origin
        },
        geometry: {
          type: 'Point',
          coordinates: [originCoords.lon, originCoords.lat]
        }
      }
    });

    map.addLayer({
      id: 'trip-origin',
      type: 'circle',
      source: 'trip-origin',
      paint: {
        'circle-radius': 15,
        'circle-color': '#22c55e',
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff'
      }
    });

    map.addLayer({
      id: 'trip-origin-label',
      type: 'symbol',
      source: 'trip-origin',
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 10,
        'text-anchor': 'center'
      },
      paint: {
        'text-color': '#ffffff'
      }
    });

    // Add markers for each trip destination
    trips.forEach((trip, index) => {
      const isLast = index === trips.length - 1;
      const color = isLast ? '#ef4444' : '#f59e0b'; // Red for final, orange for intermediate
      
      map.addSource(`trip-${trip.trip_number}`, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {
            label: `${trip.trip_number}`,
            city: trip.destination,
            payment: trip.payment_amount || trip.payment || 0,
            profit: trip.profit || 0
          },
          geometry: {
            type: 'Point',
            coordinates: trip.coordinates || [0, 0] // Will be updated with actual coordinates
          }
        }
      });

      map.addLayer({
        id: `trip-${trip.trip_number}`,
        type: 'circle',
        source: `trip-${trip.trip_number}`,
        paint: {
          'circle-radius': 15,
          'circle-color': color,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      });

      map.addLayer({
        id: `trip-${trip.trip_number}-label`,
        type: 'symbol',
        source: `trip-${trip.trip_number}`,
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // Add payment/profit info below marker
      map.addLayer({
        id: `trip-${trip.trip_number}-info`,
        type: 'symbol',
        source: `trip-${trip.trip_number}`,
        layout: {
          'text-field': `$${(trip.payment_amount || trip.payment || 0).toLocaleString()}\n$${(trip.profit || 0).toFixed(0)} profit`,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 9,
          'text-anchor': 'top',
          'text-offset': [0, 2]
        },
        paint: {
          'text-color': color,
          'text-halo-color': '#ffffff',
          'text-halo-width': 1
        }
      });
    });

    // Add hover effects and popups for trip markers
    trips.forEach((trip) => {
      const markerId = `trip-${trip.trip_number}`;
      
      // Add hover cursor
      map.on('mouseenter', markerId, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      
      map.on('mouseleave', markerId, () => {
        map.getCanvas().style.cursor = '';
      });
      
      // Add click popup with trip details
      map.on('click', markerId, (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const properties = e.features[0].properties;
        
        // Create popup content
        const popupContent = `
          <div style="font-family: system-ui, -apple-system, sans-serif; font-size: 14px; min-width: 200px;">
            <div style="font-weight: bold; font-size: 16px; margin-bottom: 8px; color: #1f2937;">
              Trip ${trip.trip_number}
            </div>
            <div style="margin-bottom: 6px;">
              <strong>Route:</strong> ${trip.origin || 'Previous destination'} → ${trip.destination}
            </div>
            <div style="margin-bottom: 6px;">
              <strong>Distance:</strong> ${trip.mileage || trip.distance || 'N/A'} miles
            </div>
            <div style="margin-bottom: 6px;">
              <strong>Payment:</strong> $${(trip.payment_amount || trip.payment || 0).toLocaleString()}
            </div>
            <div style="margin-bottom: 6px;">
              <strong>Profit:</strong> $${(trip.profit || 0).toFixed(0)}
            </div>
            <div style="margin-bottom: 6px;">
              <strong>Rate/Mile:</strong> $${(trip.rate_per_mile || 0).toFixed(2)}
            </div>
            ${trip.driving_time ? `<div style="margin-bottom: 6px;"><strong>Driving Time:</strong> ${trip.driving_time}h</div>` : ''}
            ${trip.outbound_opportunities ? `<div><strong>Outbound Loads:</strong> ${trip.outbound_opportunities} available</div>` : ''}
          </div>
        `;
        
        // Ensure popup coordinates are valid
        while (Math.abs(e.lngLat.lng) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        
        // Create and show popup
        import('mapbox-gl').then((mapboxgl) => {
          new mapboxgl.default.Popup({ offset: 25 })
            .setLngLat(coordinates)
            .setHTML(popupContent)
            .addTo(map);
        });
      });
    });
  };

  // Process route data and draw routes
  useEffect(() => {
    if (!map || !routeData?.route_plan?.length) return;

    const processRoutes = async () => {
      console.log('[RouteMapVisualization] Processing routes...', routeData.route_plan);
      
      // Clear existing route and deadhead layers first
      const clearRouteLayers = () => {
        const existingLayers = map.getStyle().layers.map(layer => layer.id);
        const existingSources = Object.keys(map.getStyle().sources);
        
        // Remove route and deadhead layers and sources (including labels)
        existingLayers.forEach(layerId => {
          if (layerId.startsWith('route-') || layerId.startsWith('deadhead-')) {
            try {
              map.removeLayer(layerId);
            } catch (e) {}
          }
        });
        
        existingSources.forEach(sourceId => {
          if (sourceId.startsWith('route-') || sourceId.startsWith('deadhead-')) {
            try {
              map.removeSource(sourceId);
            } catch (e) {}
          }
        });
      };
      
      clearRouteLayers();
      
      // Geocode origin
      const originCoords = await geocodeCity(origin);
      if (!originCoords) {
        console.error('[RouteMapVisualization] Could not geocode origin:', origin);
        return;
      }

      // Geocode all destinations and draw connecting routes
      const tripsWithCoords = [];
      let currentCoords = originCoords;
      
      for (let i = 0; i < routeData.route_plan.length; i++) {
        const trip = routeData.route_plan[i];
        const destCoords = await geocodeCity(trip.destination);
        
        if (destCoords) {
          tripsWithCoords.push({
            ...trip,
            coordinates: [destCoords.lon, destCoords.lat],
            coords: destCoords
          });
          
          // Draw route from current position to this trip's destination with trip details
          await drawRoute(map, currentCoords, destCoords, `route-${trip.trip_number}`, false, trip);
          
          // Update current position to this destination for next trip
          currentCoords = destCoords;
          
          // If there's a next trip, draw route from this destination to next origin
          const nextTrip = routeData.route_plan[i + 1];
          if (nextTrip) {
            // Check if next trip starts from a different location
            const nextOrigin = nextTrip.origin || trip.destination;
            const nextOriginCoords = await geocodeCity(nextOrigin);
            
            if (nextOriginCoords && 
                (Math.abs(destCoords.lat - nextOriginCoords.lat) > 0.01 || 
                 Math.abs(destCoords.lon - nextOriginCoords.lon) > 0.01)) {
              // Draw deadhead route to next pickup location
              await drawRoute(map, destCoords, nextOriginCoords, `deadhead-${trip.trip_number}`, true);
              currentCoords = nextOriginCoords;
            }
          }
        }
      }

      // Add trip markers
      addTripMarkers(map, tripsWithCoords, originCoords);

      // Fit map to show all points
      if (tripsWithCoords.length > 0) {
        // Import mapbox-gl for LngLatBounds
        const mapboxgl = await import('mapbox-gl');
        
        const allCoords = [
          [originCoords.lon, originCoords.lat],
          ...tripsWithCoords.map(trip => trip.coordinates)
        ];
        
        // Create bounds ensuring proper [lng, lat] format
        const bounds = new mapboxgl.default.LngLatBounds();
        
        // Extend bounds with all coordinates
        allCoords.forEach(coord => {
          if (Array.isArray(coord) && coord.length >= 2) {
            bounds.extend(coord);
          }
        });
        
        // Only fit bounds if we have valid coordinates
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 50 });
        }
      }
    };

    processRoutes();
  }, [map, routeData, origin]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 px-6 py-4">
        <div className="flex items-center space-x-2">
          <Navigation className="w-5 h-5 text-white" />
          <h3 className="text-lg font-semibold text-white">Route Visualization</h3>
          {routeData?.route_plan?.length && (
            <span className="text-blue-100 text-sm">
              {routeData.route_plan.length} trips planned
            </span>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div className="relative">
        <div 
          ref={mapContainer} 
          className="w-full h-96"
          style={{ 
            minHeight: '384px',
            backgroundColor: isDarkMode ? '#374151' : '#f0f0f0'
          }}
        />
        
        {(!process.env.REACT_APP_MAPBOX_PUBLIC_KEY || !process.env.REACT_APP_MAPBOX_PUBLIC_KEY.startsWith('pk.')) && (
          <div className="absolute inset-0 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <div className="text-center p-4">
              <MapPin className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {!process.env.REACT_APP_MAPBOX_PUBLIC_KEY 
                  ? 'Mapbox API key required for route visualization'
                  : 'Mapbox GL requires a public token (pk.*), not secret token (sk.*)'
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-gray-600 dark:text-gray-400">Start</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-gray-600 dark:text-gray-400">Pickup/Dropoff</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-gray-600 dark:text-gray-400">Final Destination</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-4 h-0.5 bg-blue-500"></div>
              <span className="text-gray-600 dark:text-gray-400">Route</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-0.5 bg-gray-400 border-dashed border-t"></div>
              <span className="text-gray-600 dark:text-gray-400">Deadhead</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RouteMapVisualization;